import { useState } from 'react';
import { Link } from 'react-router';
import { ROLE_LABELS, type Role } from '@rundown/policy';
import { Popover } from '../../components';
import { avatarColour, initialOf } from '../../lib/avatar';
import { cx } from '../../lib/cx';

/**
 * Who you are signed in as, and the way out — in the top-right corner.
 *
 * This lived at the bottom of the sidebar, pushed there by a `flex-1` spacer.
 * That works only while the rail is shorter than the viewport, and it stopped
 * being true the moment large-display scaling shipped: on a wide-but-short
 * screen the nav grew past the available height and the person's own name and
 * Sign out were the first things pushed out of sight.
 *
 * Anchoring identity to the header instead removes the failure mode rather
 * than tuning around it — the header has a fixed height and cannot be crowded
 * out by a nav that grows as more screens are built. It is also where people
 * look for it.
 */

export interface UserMenuProps {
  readonly name: string;
  readonly email: string;
  readonly role: Role;
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
}

export function UserMenu({ name, email, role, onSignOut, signingOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);

  const label = name || email;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={`Signed in as ${label}`}
      className="w-56 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_-12px_rgba(0,0,0,0.18)]"
      trigger={
        <button
          type="button"
          aria-label={`Signed in as ${label}`}
          title={label}
          className={cx(
            'grid size-[1.875rem] place-items-center rounded-full fs-12 font-bold text-white',
            'ring-offset-2 ring-offset-base hover:ring-2 hover:ring-line-stronger',
            avatarColour(label),
          )}
        >
          {initialOf(label)}
        </button>
      }
    >
      {/* The outside-click and Escape handling that used to live here was a
          verbatim copy of AppShell's, effect for effect. Both are Radix's
          now — see components/Popover.tsx. */}
      <div role="menu">
        <div className="border-b border-surface-3 px-3.5 py-3">
          <div className="truncate fs-13 font-semibold text-ink">{label}</div>
          <div className="truncate fs-11 text-muted">{email}</div>
          <div className="mt-1 fs-11 text-muted">{ROLE_LABELS[role]}</div>
        </div>

        {/* The Account screen — password, sessions, MFA — has existed at this
              route since it was built, and nothing in the product linked to it,
              so the only way to reach it was to type the URL. This menu is where
              a person looks for their own settings, and it is the only place in
              the app that is about *you* rather than the workspace. */}
        <Link
          to="/account/security"
          role="menuitem"
          onClick={() => setOpen(false)}
          className="block w-full px-3.5 py-2.5 text-left fs-12.5 text-ink hover:bg-surface"
        >
          Account &amp; security
        </Link>

        <button
          type="button"
          role="menuitem"
          onClick={onSignOut}
          disabled={signingOut}
          className="block w-full border-t border-surface-3 px-3.5 py-2.5 text-left fs-12.5 text-ink hover:bg-surface disabled:opacity-50"
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </Popover>
  );
}
